import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, BODY_AREAS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, CheckCircle2, FileText, Heart,
  Stethoscope, Pill, StickyNote, Camera, Upload, X, History,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConsultationHistorySection from "@/components/crm/ConsultationHistory";
import VitalsForm from "@/components/crm/VitalsForm";
import DiagnosisBuilder from "@/components/crm/DiagnosisBuilder";
import PrescriptionBuilder from "@/components/crm/PrescriptionBuilder";

// Clinical advice the doctor gives; reception converts it to a real date.
// Values mirror backend routes_consultation._INTERVAL_RE.
const NEXT_VISIT_INTERVALS = [
  { value: "1w", label: "1 week" },
  { value: "2w", label: "2 weeks" },
  { value: "4w", label: "4 weeks" },
  { value: "6w", label: "6 weeks" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "prn", label: "As needed" },
];

export default function Consultation() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Reception can open this page to enter vitals, but the clinical record
  // belongs to the clinician. The server enforces this too — it 403s any
  // attempt to write a clinical field — so this is convenience, not security.
  const canEditClinical = ["doctor", "super_admin", "hq_admin"].includes(user?.role);
  const [appt, setAppt] = useState(null);
  const [consult, setConsult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previousVitals, setPreviousVitals] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pastCount, setPastCount] = useState(0);
  const autoSaveTimer = useRef(null);
  const [dirty, setDirty] = useState(false);

  // Form state
  const [vitals, setVitals] = useState({});
  const [diagnoses, setDiagnoses] = useState([]);
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [advice, setAdvice] = useState("");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [hpi, setHpi] = useState("");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [nextVisitInterval, setNextVisitInterval] = useState("");
  const [nextVisitNotes, setNextVisitNotes] = useState("");
  const [treatmentId, setTreatmentId] = useState("");

  // Photo upload
  const [photos, setPhotos] = useState([]);
  const [photoTag, setPhotoTag] = useState("before");
  const [photoArea, setPhotoArea] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadConsultation = useCallback(async () => {
    try {
      const apptRes = await api.get(`/appointments`);
      const found = apptRes.data.find((a) => a.id === appointmentId);
      if (!found) { toast.error("Appointment not found"); navigate(-1); return; }
      setAppt(found);

      let consultData;
      try {
        const cRes = await api.get(`/consultations/by-appointment/${appointmentId}`);
        consultData = cRes.data;
      } catch {
        const createRes = await api.post("/consultations", {
          appointment_id: appointmentId,
          patient_id: found.patient_id || "",
        });
        consultData = createRes.data;
      }
      setConsult(consultData);
      populateForm(consultData);

      // Creating the consultation no longer flips the appointment — reception
      // creates the shell at check-in to record vitals, and that must not pull
      // the patient out of the waiting room. So the DOCTOR opening this page is
      // what starts the consultation.
      if (canEditClinical && found.status === "checked_in") {
        try {
          await api.put(`/appointments/${appointmentId}`, { status: "in_consultation" });
          setAppt((a) => ({ ...a, status: "in_consultation" }));
        } catch { /* non-fatal — the clinical form still works */ }
      }

      // Load previous vitals for reference
      if (found.patient_id) {
        try {
          const histRes = await api.get(`/patients/${found.patient_id}/consultation-history`);
          const prior = (histRes.data.consultations || []).filter((c) => c.id !== consultData.id);
          setPastCount(prior.length);
          const past = prior.find((c) => c.vitals);
          if (past?.vitals) setPreviousVitals(past.vitals);
        } catch { /* ignore */ }
        try {
          const pk = await api.get(`/patients/${found.patient_id}/packages`);
          setPackages((pk.data.items || []).filter((t) => t.status !== "completed"));
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [appointmentId, navigate, canEditClinical]);

  const populateForm = (c) => {
    setVitals(c.vitals || {});
    setDiagnoses(c.diagnoses || []);
    setPrescriptionItems(c.prescription_items || []);
    setAdvice(c.advice || "");
    setChiefComplaint(c.chief_complaint || "");
    setHpi(c.history_of_present_illness || "");
    setClinicalNotes(c.clinical_notes || "");
    setNextVisitInterval(c.next_visit_interval || "");
    setNextVisitNotes(c.next_visit_notes || "");
    setTreatmentId(c.treatment_id || "");
    setPhotos(c.clinical_photos || []);
    setDirty(false);
  };

  useEffect(() => { loadConsultation(); }, [loadConsultation]);

  // Auto-save every 30s when dirty
  useEffect(() => {
    if (!dirty || !consult) return;
    autoSaveTimer.current = setTimeout(() => {
      saveConsultation(true);
    }, 30000);
    return () => clearTimeout(autoSaveTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, vitals, diagnoses, prescriptionItems, advice, chiefComplaint, hpi, clinicalNotes, nextVisitInterval]);

  const markDirty = () => setDirty(true);

  // Local preview of what the interval works out to, so the doctor sees the
  // consequence of "4 weeks" without waiting for reception.
  const suggestedNextVisit = React.useMemo(() => {
    const m = /^(\d{1,3})([dwm])$/.exec(nextVisitInterval || "");
    if (!m) return "";
    const n = Number(m[1]);
    const base = appt?.scheduled_at ? new Date(appt.scheduled_at) : new Date();
    const d = new Date(base);
    if (m[2] === "d") d.setDate(d.getDate() + n);
    else if (m[2] === "w") d.setDate(d.getDate() + n * 7);
    else d.setMonth(d.getMonth() + n);
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  }, [nextVisitInterval, appt]);

  // Reception may only write vitals here — the server rejects anything else
  // with a 403, so the payload must narrow to match or every autosave fails.
  const buildPayload = () => (canEditClinical ? {
    vitals: Object.keys(vitals).length > 0 ? vitals : null,
    diagnoses,
    prescription_items: prescriptionItems,
    advice,
    chief_complaint: chiefComplaint,
    history_of_present_illness: hpi,
    clinical_notes: clinicalNotes,
    // The doctor advises an INTERVAL; reception turns it into a date at
    // checkout. Deliberately no next_visit_date here — the recall worklist
    // queries that field, so writing it from advice would chase a date the
    // patient never agreed to.
    next_visit_interval: nextVisitInterval || null,
    next_visit_notes: nextVisitNotes,
    treatment_id: treatmentId || null,
  } : {
    vitals: Object.keys(vitals).length > 0 ? vitals : null,
  });

  const saveConsultation = async (silent = false) => {
    if (!consult) return;
    setSaving(true);
    try {
      await api.put(`/consultations/${consult.id}`, buildPayload());
      setDirty(false);
      if (!silent) toast.success("Saved");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // Package-linked visit? Ask whether it used a session before finishing —
  // otherwise the package sits at 0/6 while the patient has actually been seen,
  // and the recall worklist keeps chasing them.
  const requestComplete = () => {
    if (consult?.treatment_id) { setConfirmingComplete(true); return; }
    completeConsultation(false);
  };

  const completeConsultation = async (consumesSession) => {
    if (!consult) return;
    setCompleting(true);
    try {
      await api.put(`/consultations/${consult.id}`, buildPayload());
      await api.post(`/consultations/${consult.id}/complete`,
                     { consumes_session: !!consumesSession });
      toast.success(consumesSession
        ? "Session recorded · sent to reception for checkout"
        : "Sent to reception for checkout");
      // The doctor used to be dropped onto the invoice page from here. Billing
      // and the next-visit date are reception's job now — this navigation is
      // what makes the handoff real.
      navigate("/doctor/my-day");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  const generatePdf = async () => {
    if (!consult) return;
    try {
      await api.put(`/consultations/${consult.id}`, buildPayload());
      await api.post(`/consultations/${consult.id}/generate-pdf`);
      const pdfRes = await api.get(`/consultations/${consult.id}/prescription-pdf`, { responseType: "blob" });
      const blob = new Blob([pdfRes.data], { type: "application/pdf" });
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadPhoto = async (file) => {
    if (!consult || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag", photoTag);
      fd.append("body_area", photoArea);
      const res = await api.post(`/consultations/${consult.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [...prev, res.data.photo_ref]);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" /></div>;
  if (!appt || !consult) return <p className="py-12 text-center text-muted-foreground">Consultation not found.</p>;

  const patientName = appt.patient_name || "Patient";

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="font-display text-xl font-semibold text-navy">{patientName}</h2>
            <p className="text-xs text-muted-foreground">
              {[appt.service, formatDateTime(appt.scheduled_at)].filter(Boolean).join(" · ")}
              {dirty && <span className="ml-2 text-gold">● Unsaved</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => saveConsultation(false)} disabled={saving} className="border-navy/20 text-navy">
            <Save className="mr-1.5 h-3.5 w-3.5" /> {saving ? "Saving..." : "Save"}
          </Button>
          <Button variant="outline" size="sm" onClick={generatePdf} className="border-gold/30 text-gold hover:bg-gold/5">
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Prescription PDF
          </Button>
          {canEditClinical && (
            <Button size="sm" onClick={requestComplete} disabled={completing} className="bg-green-700 text-white hover:bg-green-800">
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> {completing ? "Completing..." : "Complete"}
            </Button>
          )}
        </div>
      </div>

      {!canEditClinical && (
        <p className="rounded-lg bg-cream/60 px-3 py-2 text-xs text-muted-foreground">
          You can record <strong>vitals</strong> here. Diagnosis and prescription are the doctor's
          to fill and are shown read-only.
        </p>
      )}
      {consult.vitals_recorded_by_name && (
        <p className="text-xs text-muted-foreground">
          Vitals recorded by {consult.vitals_recorded_by_name}
          {consult.vitals_recorded_at ? ` · ${formatDateTime(consult.vitals_recorded_at)}` : ""}
        </p>
      )}

      {/* Tabs */}
      <Tabs defaultValue="vitals" className="w-full">
        <TabsList className="grid w-full grid-cols-6 bg-cream">
          <TabsTrigger value="vitals" className="gap-1.5 text-xs"><Heart className="h-3.5 w-3.5" /> Vitals</TabsTrigger>
          <TabsTrigger value="diagnosis" className="gap-1.5 text-xs"><Stethoscope className="h-3.5 w-3.5" /> Diagnosis</TabsTrigger>
          <TabsTrigger value="prescription" className="gap-1.5 text-xs"><Pill className="h-3.5 w-3.5" /> Prescription</TabsTrigger>
          <TabsTrigger value="notes" className="gap-1.5 text-xs"><StickyNote className="h-3.5 w-3.5" /> Notes</TabsTrigger>
          <TabsTrigger value="photos" className="gap-1.5 text-xs"><Camera className="h-3.5 w-3.5" /> Photos</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5 text-xs" data-testid="tab-history">
            <History className="h-3.5 w-3.5" /> History
            {pastCount > 0 && (
              <span className="ml-0.5 rounded-full bg-navy/10 px-1.5 text-[10px] font-semibold text-navy">{pastCount}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="mt-4 rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
          <TabsContent value="vitals" className="mt-0">
            <VitalsForm
              vitals={vitals}
              onChange={(v) => { setVitals(v); markDirty(); }}
              previousVitals={previousVitals}
            />
          </TabsContent>

          <TabsContent value="diagnosis" className="mt-0">
            {/* pointer-events-none is the least invasive read-only: these
                builders have no `disabled` prop, and the server 403s any write
                from a non-clinician regardless. */}
            <div className={canEditClinical ? "" : "pointer-events-none opacity-70"}>
              <DiagnosisBuilder
                diagnoses={diagnoses}
                onChange={(d) => { setDiagnoses(d); markDirty(); }}
              />
            </div>
          </TabsContent>

          <TabsContent value="prescription" className="mt-0">
            <div className={canEditClinical ? "" : "pointer-events-none opacity-70"}>
              <PrescriptionBuilder
                items={prescriptionItems}
                onChange={(items) => { setPrescriptionItems(items); markDirty(); }}
                advice={advice}
                onAdviceChange={(v) => { setAdvice(v); markDirty(); }}
              />
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-0 space-y-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Chief Complaint</label>
              <Input value={chiefComplaint} disabled={!canEditClinical} onChange={(e) => { setChiefComplaint(e.target.value); markDirty(); }} placeholder="Primary reason for visit..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">History of Present Illness</label>
              <Textarea rows={3} value={hpi} disabled={!canEditClinical} onChange={(e) => { setHpi(e.target.value); markDirty(); }} placeholder="Duration, progression, associated symptoms..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Clinical Notes</label>
              <Textarea rows={4} value={clinicalNotes} disabled={!canEditClinical} onChange={(e) => { setClinicalNotes(e.target.value); markDirty(); }} placeholder="Examination findings, plan, observations..." />
            </div>
            {/* Clinical ADVICE, not a booking. Reception confirms the actual
                date at checkout — you pick the interval. */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Next Visit In</label>
              <div className="flex flex-wrap gap-1.5">
                {NEXT_VISIT_INTERVALS.map((iv) => (
                  <button
                    key={iv.value}
                    type="button"
                    disabled={!canEditClinical}
                    onClick={() => { setNextVisitInterval(iv.value === nextVisitInterval ? "" : iv.value); markDirty(); }}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                      nextVisitInterval === iv.value
                        ? "border-navy bg-navy text-white"
                        : "border-input text-muted-foreground hover:bg-nude/20"}`}
                    data-testid={`next-visit-${iv.value}`}
                  >
                    {iv.label}
                  </button>
                ))}
              </div>
              {suggestedNextVisit && (
                <p className="text-xs text-muted-foreground">
                  Suggested {suggestedNextVisit} — reception will confirm the date.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Next Visit Notes</label>
              <Input value={nextVisitNotes} disabled={!canEditClinical} onChange={(e) => { setNextVisitNotes(e.target.value); markDirty(); }} placeholder="Follow-up instructions..." />
            </div>
          </TabsContent>

          <TabsContent value="photos" className="mt-0 space-y-4">
            {/* Upload controls */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground">Tag</label>
                <Select value={photoTag} onValueChange={setPhotoTag}>
                  <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="during">During</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-muted-foreground">Body Area</label>
                <Select value={photoArea} onValueChange={setPhotoArea}>
                  <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Area" /></SelectTrigger>
                  <SelectContent>
                    {BODY_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-navy/20 px-3 py-1.5 text-sm text-muted-foreground hover:bg-cream/40">
                <Upload className="h-4 w-4" />
                {uploading ? "Uploading..." : "Upload Photo"}
                <input type="file" className="hidden" accept="image/*" disabled={uploading}
                  onChange={(e) => { if (e.target.files[0]) uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>

            {/* Photo grid */}
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((p, i) => (
                  <div key={i} className="overflow-hidden rounded-lg border border-navy/10">
                    <img
                      src={`${api.defaults.baseURL}/documents/${p.document_id}/download`}
                      alt={p.caption || `Photo ${i + 1}`}
                      className="aspect-square w-full object-cover"
                    />
                    <div className="bg-cream/40 px-2 py-1.5">
                      <p className="text-[11px] font-semibold capitalize text-navy">{p.tag}</p>
                      <p className="text-[10px] text-muted-foreground">{p.body_area || "—"}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-navy/10 py-12 text-center">
                <Camera className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No clinical photos yet.</p>
              </div>
            )}
          </TabsContent>

          {/* What the doctor needs at hand mid-consultation: where this patient
              is in their course of treatment, and what was prescribed before. */}
          <TabsContent value="history" className="mt-0 space-y-5">
            {packages.length > 0 && (
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Treatments in progress
                </h4>
                <div className="space-y-2">
                  {packages.map((t) => {
                    const done = t.sessions_done || 0;
                    const total = t.sessions_total || 1;
                    return (
                      <div key={t.id} className="flex items-center gap-3 rounded-lg border border-navy/10 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink">{t.package_name || t.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Session {Math.min(done + 1, total)} of {total}
                            {t.days_to_expiry != null && t.days_to_expiry <= 30 && (
                              <span className={t.days_to_expiry < 0 ? "text-red-600" : "text-amber-700"}>
                                {t.days_to_expiry < 0 ? " · expired" : ` · expires in ${t.days_to_expiry}d`}
                              </span>
                            )}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-navy/10 px-2 py-0.5 text-[11px] font-semibold text-navy">
                          {done}/{total}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {appt.patient_id
              ? <ConsultationHistorySection patientId={appt.patient_id} excludeId={consult.id} />
              : <p className="py-8 text-center text-sm text-muted-foreground">No patient record yet — history appears after check-in.</p>}
          </TabsContent>
        </div>
      </Tabs>

      {/* Did this visit use one of the package's sessions? Asked rather than
          assumed — a review or a follow-up shouldn't burn a paid session, and
          nobody should have to remember to log it separately afterwards. */}
      <Dialog open={confirmingComplete} onOpenChange={(o) => { if (!o) setConfirmingComplete(false); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Complete Consultation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Did this visit use a session of{" "}
              <span className="font-medium text-ink">{consult?.treatment_name || "the patient's package"}</span>?
            </p>
            <p className="text-xs text-muted-foreground">
              Say no for a review or follow-up — it records the consultation without deducting a paid session.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirmingComplete(false); completeConsultation(false); }}
              disabled={completing} data-testid="complete-no-session">
              No, just the consult
            </Button>
            <Button onClick={() => { setConfirmingComplete(false); completeConsultation(true); }}
              disabled={completing} className="bg-navy text-white hover:bg-navy-hover" data-testid="complete-with-session">
              Yes, count a session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!pdfUrl} onOpenChange={(o) => { if (!o) { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }}>
        <DialogContent className="sm:max-w-2xl h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Prescription Preview</DialogTitle>
          </DialogHeader>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              className="h-full w-full rounded-lg border"
              title="Prescription PDF"
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }}>Close</Button>
            <Button asChild className="bg-navy text-white hover:bg-navy-hover">
              <a href={pdfUrl || "#"} download={`prescription_${consult?.id?.slice(0, 8) || ""}.pdf`}>
                Download
              </a>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
